import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Heartbeat interval (2 minutes)
const HEARTBEAT_INTERVAL = 2 * 60 * 1000;

// Token refresh interval (refresh 5 minutes before expiry - check every 2 minutes)
const TOKEN_CHECK_INTERVAL = 2 * 60 * 1000;
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiry

// Parse JWT token to get expiration time
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

// Storage helper — uses localStorage when "Remember me" is checked, sessionStorage otherwise
const getStorage = () => {
  return localStorage.getItem('rememberMe') === 'true' ? localStorage : sessionStorage;
};

const getStoredToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

const storeToken = (token, remember) => {
  // Clear both storages first to avoid stale tokens
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  if (remember) {
    localStorage.setItem('token', token);
    localStorage.setItem('rememberMe', 'true');
  } else {
    sessionStorage.setItem('token', token);
    localStorage.removeItem('rememberMe');
  }
};

const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('rememberMe');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
};

// Force re-login: bump this version whenever you need all users to re-authenticate
const AUTH_VERSION = '2';
(function forceReloginOnVersionChange() {
  const stored = localStorage.getItem('auth_version');
  if (stored !== AUTH_VERSION) {
    clearStoredAuth();
    localStorage.setItem('auth_version', AUTH_VERSION);
  }
})();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [visibleTabs, setVisibleTabs] = useState([]);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  const heartbeatIntervalRef = useRef(null);
  const tokenRefreshIntervalRef = useRef(null);
  const logoutCalledRef = useRef(false);

  // Logout function - memoized to prevent recreating on every render
  const logout = useCallback(async (showMessage = false) => {
    // Prevent multiple logout calls
    if (logoutCalledRef.current) return;
    logoutCalledRef.current = true;
    
    // Clear heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    // Clear token refresh interval
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }
    
    // Clear auth state
    clearStoredAuth();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setPermissions([]);
    setVisibleTabs([]);
    
    if (showMessage) {
      toast.error('Session expired. Please log in again.');
    }
    
    // Reset flag after a short delay
    setTimeout(() => {
      logoutCalledRef.current = false;
    }, 1000);
  }, []);

  // Token refresh function
  const refreshToken = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/refresh-token`);
      const newToken = response.data.access_token;
      
      if (newToken) {
        const remember = localStorage.getItem('rememberMe') === 'true';
        storeToken(newToken, remember);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        console.log('Token refreshed successfully');
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Only logout on explicit 401 from server, NOT on network errors
      if (error.response?.status === 401) {
        logout(true);
      } else if (!error.response) {
        // Network error — don't logout, just log warning
        console.warn('Token refresh failed due to network error, will retry later');
      }
    }
    return false;
  }, [logout]);

  // Check if token needs refresh
  const checkTokenExpiry = useCallback(() => {
    const currentToken = getStoredToken();
    if (!currentToken) return;
    
    const payload = parseJwt(currentToken);
    if (!payload || !payload.exp) return;
    
    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;
    
    // If token expires within threshold, refresh it
    if (timeUntilExpiry <= TOKEN_REFRESH_THRESHOLD && timeUntilExpiry > 0) {
      console.log(`Token expires in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes, refreshing...`);
      refreshToken();
    } else if (timeUntilExpiry <= 0) {
      console.log('Token already expired');
      logout(true);
    }
  }, [refreshToken, logout]);

  // Setup token refresh interval
  useEffect(() => {
    if (token) {
      // Check token expiry immediately
      checkTokenExpiry();
      
      // Setup interval to check token expiry
      tokenRefreshIntervalRef.current = setInterval(checkTokenExpiry, TOKEN_CHECK_INTERVAL);
    }
    
    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
    };
  }, [token, checkTokenExpiry]);

  // Setup axios interceptor for handling 401 errors globally
  useEffect(() => {
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (token) => {
      failedQueue.forEach(({ resolve, reject, config }) => {
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
          resolve(axios(config));
        } else {
          reject(new Error('Token refresh failed'));
        }
      });
      failedQueue = [];
    };

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Skip retry for login/refresh endpoints to avoid loops
        if (originalRequest?.url?.includes('/auth/login') || originalRequest?.url?.includes('/auth/refresh-token')) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          const errorMsg = error.response?.data?.detail || '';
          if (
            errorMsg.toLowerCase().includes('expired') ||
            errorMsg.toLowerCase().includes('invalid token') ||
            errorMsg.toLowerCase().includes('not authenticated')
          ) {
            if (isRefreshing) {
              // Queue this request while token is being refreshed
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject, config: originalRequest });
              });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
              const currentToken = getStoredToken();
              if (currentToken) {
                const refreshResp = await axios.post(`${API_URL}/auth/refresh-token`, null, {
                  headers: { 'Authorization': `Bearer ${currentToken}` }
                });
                if (refreshResp.data?.access_token) {
                  const newToken = refreshResp.data.access_token;
                  const remember = localStorage.getItem('rememberMe') === 'true';
                  storeToken(newToken, remember);
                  setToken(newToken);
                  axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                  isRefreshing = false;
                  processQueue(newToken);
                  // Retry original request
                  originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                  return axios(originalRequest);
                }
              }
            } catch (refreshError) {
              isRefreshing = false;
              processQueue(null);
              // Only logout on 401 from refresh, not on network errors
              if (refreshError.response?.status === 401) {
                logout(true);
              } else if (!refreshError.response) {
                console.warn('Token refresh failed due to network — keeping session');
                return Promise.reject(error);
              }
            }
            isRefreshing = false;
            processQueue(null);
            logout(true);
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    
    return () => {
      // Cleanup heartbeat on unmount
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      const userData = response.data;
      setUser(userData);
      // Use page_permissions if available (new format), fallback to permissions (legacy)
      setPermissions(userData.page_permissions || userData.permissions || []);
      setVisibleTabs(userData.visible_tabs || []);
      
      // Store user data for timezone and other utilities
      const storage = getStorage();
      storage.setItem('user', JSON.stringify({
        id: userData.id,
        name: userData.name,
        email: userData.email,
        country_id: userData.country_id,
        country_code: userData.country_code,
        country_name: userData.country_name,
        role_code: userData.role_code
      }));
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout on explicit auth failures (401), NOT on network/server errors
      if (error.response?.status === 401) {
        logout();
      } else {
        // Network error or server issue — keep user logged in, retry later
        console.warn('Network error fetching user profile, will retry on next navigation');
      }
    } finally {
      setLoading(false);
    }
  };

  // Start session tracking for attendance
  const startSessionTracking = async () => {
    try {
      await axios.post(`${API_URL}/hr/session/start`);
      
      // Start heartbeat
      heartbeatIntervalRef.current = setInterval(async () => {
        try {
          await axios.post(`${API_URL}/hr/session/heartbeat`);
        } catch (error) {
          if (error.response?.status === 401) {
            // Session expired due to inactivity
            console.log('Session expired due to inactivity');
            logout();
          }
        }
      }, HEARTBEAT_INTERVAL);
    } catch (error) {
      console.error('Failed to start session tracking:', error);
    }
  };

  // End session tracking
  const endSessionTracking = async () => {
    try {
      await axios.post(`${API_URL}/hr/session/end`);
    } catch (error) {
      console.error('Failed to end session:', error);
    }
    
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  const login = async (email, password, countryId, rememberMe = false) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password, country_id: countryId });
    const { access_token, user: userData } = response.data;
    storeToken(access_token, rememberMe);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    // Fetch full user data with permissions
    const meResponse = await axios.get(`${API_URL}/auth/me`);
    const fullUserData = meResponse.data;
    setUser(fullUserData);
    setPermissions(fullUserData.permissions || []);
    setVisibleTabs(fullUserData.visible_tabs || []);
    
    // Store user data for timezone and other utilities
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('user', JSON.stringify({
      id: fullUserData.id,
      name: fullUserData.name,
      email: fullUserData.email,
      country_id: fullUserData.country_id,
      country_code: fullUserData.country_code,
      country_name: fullUserData.country_name,
      role_code: fullUserData.role_code
    }));
    
    // Start session tracking for attendance
    startSessionTracking();
    
    return fullUserData;
  };

  const handleLogout = async () => {
    // End session tracking before logout
    await endSessionTracking();
    logout();
  };

  // Check if user has a specific permission
  const hasPermission = (permissionName) => {
    return permissions.some(p => p.name === permissionName);
  };

  // Check if tab is visible for user
  const canViewTab = (tabName) => {
    return visibleTabs.includes(tabName);
  };

  // Get permission scope for a specific permission
  const getPermissionScope = (permissionName) => {
    const perm = permissions.find(p => p.name === permissionName);
    return perm ? perm.scope : null;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout: handleLogout, 
      isAuthenticated: !!user,
      permissions,
      visibleTabs,
      hasPermission,
      canViewTab,
      getPermissionScope
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
